import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { CopyButton } from '@/ui/CopyButton'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { connectionFor, detailOf, isNotConfigured, pathFor, stepStatus } from './steps'

/**
 * Mirrors `SenderRequest.from_name` in `openapi.json` (`maxLength: 200`). Bounded
 * here as well as on the server so the refusal is immediate and attached to the
 * control, rather than a "Request validation failed" naming no field (WEB-14).
 */
const FROM_NAME_MAX = 200

const PROVIDER = 'sender_identity'

interface DnsRow {
  kind?: unknown
  name?: unknown
  value?: unknown
  verified?: unknown
}

/**
 * Step 4 — S-D2's `s4`.
 *
 * The DNS records come back from the provider and are rendered with a copy
 * button each, because "outreach silently landing in spam" is the most expensive
 * failure in this product and it is entirely preventable at this screen. They
 * are read defensively out of `detail` — the shape belongs to S-B18, and a row
 * missing a field should drop that row rather than crash the step somebody is
 * standing on.
 *
 * Nobody has to wait here. DNS takes up to an hour to propagate, the server
 * re-checks, and the copy says so — a wizard step that asked a tenant to sit and
 * refresh would be a wizard step most people abandon.
 */
export function SenderStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'sender')
  // ONB-13. S-D2 draws step 4 with both fields filled on return, and only the
  // address was: a connection row carries one label and the address has it. The
  // name is now recorded beside the provider's own detail when the step is
  // submitted (`app/v1/onboarding.configure_sender`), so a tenant coming back to
  // a step they have already answered is not asked to remember what they typed.
  const [fromName, setFromName] = useState(() => {
    const stored = detailOf(connection).from_name
    return typeof stored === 'string' ? stored : ''
  })
  const [fromEmail, setFromEmail] = useState(connection?.account_label ?? '')

  const save = useMutation((body: { from_name: string; from_email: string }) =>
    call(() => api.POST('/api/v1/onboarding/sender', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({ from_name: fromName.trim(), from_email: fromEmail.trim() })
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  const records = dnsRecords(detailOf(connection).dns)
  const verified = records.filter((row) => row.verified).length
  // The deployment has no sending provider behind this step. Not a refusal of
  // anything the tenant typed, so the form is not marked required, Continue is
  // not offered — it would only produce the same notice again — and Skip
  // becomes the one action, drawn as the primary one.
  const unavailable = step?.status === 'failed' && isNotConfigured(step)

  return (
    <form
      className="app-wiz"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <WizardChrome current="sender" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">Who are emails from?</h2>
            {/* Not "land in your unified inbox": email replies are not carried
                by the inbox — `app/outreach/inbox` reads WhatsApp threads, and
                an outreach email goes out with this address as its Reply-To
                (`app/outreach/followups`). So they arrive in this mailbox, and
                the sentence says where. Corrected 2026-09-19. */}
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              Outreach emails are sent from this address, and replies arrive in its own mailbox. We
              email it a confirmation link first.
            </p>
          </div>

          <InlineError failure={save.state.failure} />

          <div className="ar-grid ar-grid--2">
            <TextField
              label="From name"
              required={!unavailable}
              autoFocus
              maxLength={FROM_NAME_MAX}
              placeholder="Priya at Kavya Interiors"
              value={fromName}
              onChange={(event) => {
                setFromName(event.target.value)
                save.reset()
              }}
            />
            <TextField
              label="From address"
              type="email"
              required={!unavailable}
              placeholder="priya@kavyainteriors.in"
              value={fromEmail}
              onChange={(event) => {
                setFromEmail(event.target.value)
                save.reset()
              }}
            />
          </div>

          {records.length > 0 ? (
            <div className="ar-panel ar-stack ar-stack--tight">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <strong>Domain authentication</strong>
                {/* ONB-36. S-D2 draws "⚠ 1 of 2 verified" in the warning tone,
                    and the app drew every count — including "0 of 2" — in the
                    neutral grey it uses for a fact nobody has to act on. An
                    unpublished DKIM record is the single most expensive thing on
                    this screen to leave unpublished, and a badge that looks the
                    same whether it says 0 or 2 is a badge nobody reads twice.
                    Success once every row is verified; warning until then. */}
                <span
                  className={`ar-badge ar-badge--${verified === records.length ? 'success' : 'warning'}`}
                >
                  {verified === records.length ? '✓' : '⚠'} {verified} of {records.length} verified
                </span>
              </div>
              <div className="ar-table-wrap" tabIndex={0}>
                <table className="ar-table" style={{ background: 'transparent' }}>
                  <tbody>
                    {records.map((row) => (
                      <tr key={`${row.kind}-${row.name}`}>
                        <td>
                          <span
                            className={`ar-badge ar-badge--${row.verified ? 'success' : 'warning'}`}
                          >
                            {/* ONB-34. `⚠` on the warning tone, as the mockup's
                                own "⚠ DKIM" does. */}
                            {row.verified ? '✓' : '⚠'} {row.kind}
                          </span>
                        </td>
                        <td
                          className="ar-mono"
                          style={{
                            fontSize: 'var(--ar-text-caption-size)',
                            wordBreak: 'break-all',
                          }}
                        >
                          {row.name} → {row.value}
                        </td>
                        <td className="ar-num">
                          <CopyButton value={row.value} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p
                className="ar-muted"
                style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                DNS changes take up to an hour. We re-check automatically and email you when
                it&rsquo;s done — you don&rsquo;t have to wait here.
              </p>
            </div>
          ) : null}

          {unavailable ? (
            <div className="ar-alert ar-alert--info" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                i
              </span>
              <div className="ar-alert__body">
                <strong>Sending addresses are not switched on here yet</strong>
                Nothing you typed is wrong — this deployment has no email provider connected, so
                there is nothing to set up. Skip this step; nothing else in your setup depends on
                it, and outreach stays off until an address exists.
              </div>
            </div>
          ) : step?.status === 'failed' && detailOf(step).message ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              {/* ONB-35. A refusal, not a caution (ONB-34). */}
              <span className="ar-alert__icon" aria-hidden="true">
                ✕
              </span>
              <div className="ar-alert__body">
                <strong>We could not set up a sending address</strong>
                {String(detailOf(step).message)}
              </div>
            </div>
          ) : step?.status === 'pending' && detailOf(step).message ? (
            // `pending` is this step's success — a link is on its way — and
            // until 2026-09-19 it rendered nothing: *Continue* answered 200,
            // the step stayed here, and the screen did not change. The live
            // tenant pressed it, saw nothing, and left outreach off for nine
            // days. The server's sentence is the one shown, because it is the
            // one that knows whether a link was sent (`pending`) or the
            // provider refused the address (`retrying`).
            <div
              className={`ar-alert ar-alert--${detailOf(step).state === 'retrying' ? 'warning' : 'info'}`}
              role="status"
            >
              <span className="ar-alert__icon" aria-hidden="true">
                {detailOf(step).state === 'retrying' ? '⚠' : '✉'}
              </span>
              <div className="ar-alert__body">
                <strong>
                  {detailOf(step).state === 'retrying'
                    ? 'This address is not set up yet'
                    : 'Check that inbox for our email'}
                </strong>
                {String(detailOf(step).message)}
              </div>
            </div>
          ) : null}
        </div>

        <WizardFooter current="sender" skipTone={unavailable ? 'primary' : 'quiet'}>
          {unavailable ? null : (
            <Button tone="primary" type="submit" busy={save.pending} busyLabel="Setting up">
              Continue
            </Button>
          )}
        </WizardFooter>
      </section>
    </form>
  )
}

interface DnsRecord {
  kind: string
  name: string
  value: string
  verified: boolean
}

/** Read the provider's DNS rows defensively — S-B18 owns the shape, not this file. */
function dnsRecords(value: unknown): DnsRecord[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row: DnsRow) => {
    if (typeof row.kind !== 'string' || typeof row.name !== 'string') return []
    if (typeof row.value !== 'string') return []
    return [{ kind: row.kind, name: row.name, value: row.value, verified: row.verified === true }]
  })
}
